import { useState, useMemo } from "react";
import { Modal } from "antd";
import { notification } from "antd";
import { filmStore } from "@/store/film";

import { storeUser } from "@/store/user";
import { getResponseErrorMessage, getApiBusinessError, ApiBusinessError } from "@/utils/api-service";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

import CardContent from "@/components/card/CardContent";
import SerieCardEdit from "./SerieCardEdit";
import ModalContentRemove from "@/components/modals/ModalContentRemove";

import api from "@/api/index";

import "./styles/serie-card.scss";

interface Props {
  index: number;
  data: any;
  isEdit?: boolean;
  serial_id: string | number;
  season_id?: string | number;

  remove: (index: number, data: any) => void;
  update: () => void;
}

const SerieCard = ({
  index,
  data,
  isEdit,
  serial_id,
  season_id,
  remove,
  update,
}: Props) => {
  const descTextLen = 300;

  const [isShowEditModal, setIsShowEditModal] = useState(false);
  const [editSerieItem, setEditSerieItem] = useState(null);
  const [isShowModalRemove, setIsShowModalRemove] = useState(false);
  const [voiceovers, setVoiceovers] = useState(
    data?.voiceovers ? data.voiceovers : []
  );
  const [voiceoversRemove, setVoiceoversRemove] = useState([]);
  const [descLen, setDescLen] = useState<number>(descTextLen);

  const title = `Эпизод ${data.sort_order}`;

  const removeCard = () => {
    remove(index, data);
    setIsShowModalRemove(false);
  };

  const handleRemove = () => {
    setIsShowModalRemove(true);
  };

  const closeModalRemove = () => {
    setIsShowModalRemove(false);
  };

  const handleShowEdit = () => {
    setIsShowEditModal(true);
    setEditSerieItem({
      ...data,
    });
  };

  const updateVoiceovers = (data) => {
    setVoiceovers(data);
  };

  const updateDeleteVoiceovers = (data) => {
    setVoiceoversRemove((prev) => [...prev, data]);
  };

  const removeVoiceovers = async (serie_id) => {
    for (const item of voiceoversRemove) {
      await api.serie.removeFileFromSerie(
        serial_id,
        season_id,
        serie_id,
        item.id,
        item.video_id
      );
    }

    setIsShowEditModal(false);
    setTimeout(() => {
      update();
    }, 1000);
  };

  const addNew = async (serie_id, list) => {
    for (const item of list) {
      const response = await api.serie.addFileToSerie(serial_id, season_id, serie_id, {
        voiceover_id: item.voiceover_id,
        video_file: item.video_file || "",
        has_internal_ads: item.has_internal_ads,
        isActive: item.isActive ?? undefined,
        exclusive_days: item.exclusive_days || 0,
      });

      // Бэкенд отдаёт отказ как HTTP 200 + success:false —
      // без этой проверки фронт показывал «Успешно» при фактическом отказе
      const businessError = getApiBusinessError(response);
      if (businessError) {
        throw new ApiBusinessError(businessError);
      }
    }

    removeVoiceovers(serie_id);
  };

  const queryEdit = async (serial_id, season_id, episode_id) => {
    for (const item of filmStore.voiceoversEdit) {
      const updateData: Record<string, unknown> = {
        // id озвучки в списке = content_source_id (VideoAsVoiceoverResource)
        voiceover_id: item.voiceover_id ?? item.old?.voiceover_id ?? item.id,
        has_internal_ads: item.has_internal_ads ?? item.old?.has_internal_ads ?? false,
      };
      // video_file не обязателен при обновлении — не теряем данные при отсутствии old
      const filename = item.video_file?.filename ?? item.old?.video_file?.filename;
      if (filename) {
        updateData.video_file = filename;
      }
      if (item.exclusive_days !== undefined) {
        updateData.exclusive_days = item.exclusive_days;
      }
      const response = await api.serie.updateVoiceover(
        serial_id,
        season_id,
        episode_id,
        updateData,
        item.id,
        item.video_id
      );

      const businessError = getApiBusinessError(response);
      if (businessError) {
        throw new ApiBusinessError(businessError);
      }
    }
  };

  const acceptEdit = async () => {
    try {
      const serieData = {
        ...editSerieItem,
        film_name: editSerieItem.name,
        title: editSerieItem.title
          ? editSerieItem.title
          : editSerieItem.film_name,
        sort_order: editSerieItem.sort_order,
        categories: [],
        voiceovers: voiceovers.map((item) => {
          return {
            ...item,
            active: item.active ?? undefined,
          };
        }),
      };

      const listForSave = voiceovers.filter((item) => item.isNew);

      const editResponse = await api.serie.editSerie(serial_id, season_id, serieData.id, serieData);
      const editError = getApiBusinessError(editResponse);
      if (editError) {
        throw new ApiBusinessError(editError);
      }

      await queryEdit(serial_id, season_id, serieData.id, serieData);
      await addNew(serieData.id, listForSave);

      notification.success({
        message: "Успешно",
      });
    } catch (err) {
      console.error(err);
      const messageFormatter = getResponseErrorMessage(
        err,
        "Ошибка при редактировании сериала"
      );
      notification.error({
        message: messageFormatter,
      });
    }
  };

  const closeModalEdit = () => {
    setIsShowEditModal(false);
  };

  const handleUpdateOption = (key, value) => {
    setEditSerieItem({
      ...editSerieItem,
      [key]: value,
    });
  };

  const showFullText = () => {
    setDescLen(data.description.lenght);
  };

  const textDesc = useMemo(() => {
    if (!data?.description) {
      return "";
    }

    const description = data.description;
    const trimmedText = description.slice(0, descLen);

    return descLen < description.length ? `${trimmedText}...` : trimmedText;
  }, [data, descLen]);

  const isShowBtnDescFull = useMemo(() => {
    if (
      data &&
      data.description &&
      data.description.length > descTextLen &&
      descLen <= descTextLen
    ) {
      return true;
    }

    return false;
  }, [descLen, data]);

  return (
    <CardContent title={title}>
      <div className="serial-serie">
        <div className="serial-serie__info">
          <ul className="serial-serie__list">
            <li>
              <span className="option-name">Название:</span> {data.title}
            </li>
            <li>
              <p>Описание: {textDesc}</p>
              <div>
                {isShowBtnDescFull && (
                  <a onClick={showFullText} className="serial-serie__btn-desc">
                    Показать все
                  </a>
                )}
              </div>
            </li>
            <li>
              <span className="option-name">Год выпуска:</span>{" "}
              {data.release_year}
            </li>
            {data.admin_user?.user_id  && (
            <li>
              <span className="option-name">Загрузил:</span>{" "}
              {data.admin_user.login}
            </li>
            )}
          </ul>
        </div>

        <section className="serial-serie__section">
          <h6 className="serial-serie__section__title">Озвучки</h6>
          <ul className="serial-serie__voiceovers">
            {voiceovers.map((voice) => {
              const isInactive = Number(voice.active) !== 1;
              return (
                <li
                  key={voice.id}
                  className={isInactive ? "is-disabled" : ""}
                >
                  {voice.name}
                </li>
              );
            })}
          </ul>
        </section>

        <div className="serial-serie__control">
          {storeUser.isCanRemove && (
            <button className="app-btn-icon delete" onClick={handleRemove}>
              <DeleteOutlined />
            </button>
          )}

          {isEdit && (
            <button className="app-btn-icon" onClick={handleShowEdit}>
              <EditOutlined />
            </button>
          )}
        </div>
      </div>

      <Modal
        title="Удаление элемента"
        closable={{ "aria-label": "Custom Close Button" }}
        open={isShowModalRemove}
        onOk={removeCard}
        onCancel={closeModalRemove}
        okText="Удалить"
        cancelText="Отмена"
      >
        <ModalContentRemove
          text={`Вы уверены, что хотите удалить ${data.film_name}`}
        />
      </Modal>

      <Modal
        title={`Редактирование "${data?.title || data?.film_name || ""}"`}
        closable={{ "aria-label": "Custom Close Button" }}
        open={isShowEditModal}
        onOk={acceptEdit}
        onCancel={closeModalEdit}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <SerieCardEdit
          data={data}
          update={handleUpdateOption}
          updateVoiceovers={updateVoiceovers}
          updateDeleteVoiceovers={updateDeleteVoiceovers}
          serialId={serial_id}
          seasonId={season_id}
          onVoiceoversChanged={update}
        />
      </Modal>
    </CardContent>
  );
};

export default SerieCard;
